import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
});

// Attach JWT on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally — but NOT on auth endpoints
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url || '';
    const is401 = err.response?.status === 401;
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');

    if (is401 && !isAuthEndpoint) {
      localStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  registerTenant: (formData) => api.post('/auth/register/tenant', formData),
  sendOtp: (email) => api.post(`/auth/otp/send?email=${encodeURIComponent(email)}`),
  verifyOtp: (email, otp) => api.post(`/auth/otp/verify?email=${encodeURIComponent(email)}&otp=${otp}`),
  forgotPassword: (email) => api.post(`/auth/password/forgot?email=${encodeURIComponent(email)}`),
  resetPassword: (email, otp, newPassword) => api.post(`/auth/password/reset?email=${encodeURIComponent(email)}&otp=${otp}&newPassword=${encodeURIComponent(newPassword)}`),
};

// ── Offers ────────────────────────────────────────────────────────────────────
export const offerApi = {
  create: (data) => api.post('/offer/create', data),
  view: (token) => api.get(`/offer/view/${token}`),
  viewStatus: (token) => api.get(`/offer/check/${token}`, { timeout: 5000 }),
  sign: (data) => api.post('/offer/sign', data),
  revoke: (offerId) => api.post(`/offer/revoke?offerId=${offerId}`),
  resend: (offerId, expiryHours = 72) => api.post(`/offer/${offerId}/resend?expiryHours=${expiryHours}`),
  modify: (offerId, data) => api.put(`/offer/${offerId}`, data),
  notify: (offerId) => api.post(`/offer/${offerId}/notify`),
  publish: (offerId) => api.post(`/offer/${offerId}/publish`),
  markJoined: (offerId) => api.post(`/offer/${offerId}/joined`),
  markNoShow: (offerId) => api.post(`/offer/${offerId}/no-show`),
  documentStatus: (signatureId) => api.get(`/offer/document/${signatureId}/status`),
  details: (offerId) => api.get(`/offer/${offerId}/details`),
  pdf: (offerId) => api.get(`/offer/${offerId}/pdf`, { responseType: 'blob' }),
  list: (page = 0, size = 20) => api.get(`/offer/list?page=${page}&size=${size}`),
  dashboard: () => api.get('/offer/dashboard'),
  auditLogs: (page = 0) => api.get(`/offer/audit?page=${page}`),
  offerAudit: (offerId) => api.get(`/offer/${offerId}/audit`),
};

// ── Candidates ────────────────────────────────────────────────────────────────
export const candidateApi = {
  create: (data) => api.post('/candidates', data),
  list: () => api.get('/candidates'),
  get: (id) => api.get(`/candidates/${id}`),
  update: (id, data) => api.put(`/candidates/${id}`, data),
};

// ── Tenants ───────────────────────────────────────────────────────────────────
export const tenantApi = {
  create: (data) => api.post('/tenants', data),
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  deactivate: (id) => api.put(`/tenants/${id}/deactivate`),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  updateLogo: (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.put(`/tenants/${id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const userApi = {
  list: (tenantId) => api.get(`/users?tenantId=${tenantId}`),
  get: (id) => api.get(`/users/${id}`),
  deactivate: (id) => api.put(`/users/${id}/deactivate`),
  activate: (id) => api.put(`/users/${id}/activate`),
  register: (data) => api.post('/auth/register', data),
};

export default api;
